import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Mail, MapPin, Star } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0a0a0a] border-t border-white/5">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" className="logo-script text-4xl text-[#D4AF37]">
              LuxuryStyle
            </Link>
            <p className="text-white/60 text-sm leading-relaxed">
              Premium waterproof & tarnish-free jewellery crafted with 18k gold plated stainless steel. 
              Elegance that lasts.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/luxury_style_studioo?igsh=OWk0M2I1bnQwb3gz&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="p-2 border border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="p-2 border border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-twitter"
                className="p-2 border border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-accent text-xs uppercase tracking-[0.2em] mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {['Home', 'About', 'Jewellery', 'Style', 'Collections', 'Contact'].map((item) => (
                <li key={item}>
                  <Link
                    to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                    data-testid={`footer-link-${item.toLowerCase()}`}
                    className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-white font-accent text-xs uppercase tracking-[0.2em] mb-6">
              Categories
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/jewellery?collection=bracelets"
                  data-testid="footer-link-bracelets"
                  className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                >
                  Bracelets
                </Link>
              </li>
              <li>
                <Link
                  to="/jewellery?collection=earrings"
                  data-testid="footer-link-earrings"
                  className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                >
                  Earrings
                </Link>
              </li>
              <li>
                <Link
                  to="/jewellery?collection=gold"
                  data-testid="footer-link-gold"
                  className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                >
                  Gold Collection
                </Link>
              </li>
              <li>
                <Link
                  to="/jewellery?collection=silver"
                  data-testid="footer-link-silver"
                  className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                >
                  Silver Collection
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-accent text-xs uppercase tracking-[0.2em] mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[#D4AF37] mt-0.5" strokeWidth={1.5} />
                <a
                  href="mailto:Luxurystyle00@outlook.com"
                  data-testid="contact-email"
                  className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                >
                  Luxurystyle00@outlook.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5" strokeWidth={1.5} />
                <span className="text-white/60 text-sm">
                  United Kingdom
                </span>
              </li>
              <li className="pt-2">
                <a
                  href="https://share.google/jNF6BvocJeA38Cdry"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-review-link"
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20 hover:border-[#D4AF37] text-[#D4AF37] text-sm transition-all rounded"
                >
                  <Star className="w-4 h-4 fill-[#D4AF37]" strokeWidth={1.5} />
                  <span>Leave a Google Review</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-white/40 text-xs">
              © {currentYear} LuxuryStyle. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link
                to="/privacy"
                className="text-white/40 hover:text-white/60 text-xs transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-white/40 hover:text-white/60 text-xs transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
